import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { BuildingOffice2Icon, PencilIcon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../../Catalyst/table";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "../../Catalyst/dropdown";
import { OrganizationV3 } from "../../../store/slices/OrganizationsV3Slice";
import { ORG_STATUS, ORG_STATUS_DISPLAY } from "../../../constants/organizations";
import { formatStatusDate } from "./utils";

interface OrganizationsTableProps {
  organizations: OrganizationV3[];
  // Coach is read-only: hides Rename and Deactivate (the server 403s them anyway).
  canManage: boolean;
  onOpenOrganization: (organization: OrganizationV3) => void;
  onViewUsers: (organization: OrganizationV3) => void;
  onRename: (organization: OrganizationV3) => void;
  onDeactivate: (organization: OrganizationV3) => void;
}

const OrganizationsTable = ({
  organizations,
  canManage,
  onOpenOrganization,
  onViewUsers,
  onRename,
  onDeactivate,
}: OrganizationsTableProps) => {
  return (
    <Table>
      <TableHead>
        {/* Fixed column proportions (per the design) so columns do not shift
            between pages or search results as cell content changes. */}
        <TableRow className="bg-[#F9FAFB] border border-x-[#EAECF0]">
          <TableHeader className="w-[30%]">Name</TableHeader>
          <TableHeader className="w-[28%]">Status</TableHeader>
          <TableHeader className="w-[19%]">Number of Active Users</TableHeader>
          <TableHeader className="w-[16%]">Number of Active Sub-Orgs</TableHeader>
          <TableHeader className="w-[7%] text-right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {organizations.map((organization) => {
          const statusConfig = ORG_STATUS_DISPLAY[organization.status];
          const statusDate = formatStatusDate(organization.statusDate);
          return (
            <TableRow key={organization.id}>
              <TableCell className="font-medium text-texthead">
                {/* A real button so the drill-down is keyboard reachable, not just clickable. */}
                <button
                  type="button"
                  onClick={() => onOpenOrganization(organization)}
                  className="rounded text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                >
                  {organization.name}
                </button>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {statusConfig ? (
                    <span
                      className={`rounded-full px-3 py-1 text-sm font-medium ${statusConfig.pillClass}`}
                    >
                      {statusConfig.label}
                    </span>
                  ) : (
                    <span className="text-textColor">{organization.status}</span>
                  )}
                  {/* statusDate can be null on legacy rows — render the pill alone. */}
                  {statusConfig && statusDate && (
                    <span className="text-sm font-medium text-textColor">
                      {statusConfig.datePrefix} {statusDate}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-texthead">{organization.activeUsers}</TableCell>
              <TableCell className="text-texthead">{organization.activeSubOrgs}</TableCell>
              <TableCell className="text-right">
                <Dropdown>
                  <DropdownButton outline aria-label={`Actions for ${organization.name}`}>
                    <EllipsisHorizontalIcon />
                  </DropdownButton>
                  {/* Order and grouping follow the admin menu design: Rename,
                      navigation items, then the destructive action set apart. */}
                  <DropdownMenu anchor="bottom end">
                    {canManage && (
                      <DropdownItem onClick={() => onRename(organization)}>
                        <PencilIcon />
                        <DropdownLabel>Rename</DropdownLabel>
                      </DropdownItem>
                    )}
                    <DropdownItem onClick={() => onOpenOrganization(organization)}>
                      <BuildingOffice2Icon />
                      <DropdownLabel>View Sub Organizations</DropdownLabel>
                    </DropdownItem>
                    <DropdownItem onClick={() => onViewUsers(organization)}>
                      <UserIcon />
                      <DropdownLabel>View Users</DropdownLabel>
                    </DropdownItem>
                    {/* Deactivation is one-way; only offered on active orgs. */}
                    {canManage && organization.status === ORG_STATUS.ACTIVE && (
                      <>
                        <DropdownDivider />
                        <DropdownItem
                          onClick={() => onDeactivate(organization)}
                          className="[&>[data-slot=icon]]:text-red-600 [&>[data-slot=icon]]:data-[focus]:text-red-700"
                        >
                          <XMarkIcon />
                          <DropdownLabel className="text-red-600 group-data-[focus]:text-red-700">
                            Deactivate organization
                          </DropdownLabel>
                        </DropdownItem>
                      </>
                    )}
                  </DropdownMenu>
                </Dropdown>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default OrganizationsTable;
